import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { ControlledCheckbox } from 'src/components/ui/Checkbox/Checkbox';
import { toast } from 'react-toastify';
import { ControlledInput } from 'src/components/ui/Input/Input';
import Badge, { BadgeType } from '../../ui/Badge/Badge';
import { useConnectorMutations, useConnectorLocationsQuery } from '../../../services/connector';
import { components } from '../../../generated/v1';
import { ControlledDateTimePicker } from '../../ui/DateTimePicker/DateTimePicker';
import { ControlledSelect } from '../../ui/Select/Select';
import CountrySelect from '../../ui/CountrySelect/CountrySelect';

type Props = {
  readOnly?: boolean;
  connector: any;
  onSubmit?: (data: Record<string, any>) => void;
  allowEdition?: boolean;
};

export const IntegrationImportForm = forwardRef<HTMLFormElement, Props>(
  ({ readOnly, onSubmit, connector, allowEdition }, ref) => {
    const { t } = useTranslation();
    const editImportSchema = useMemo(() => {
      return z.object({
        name: z.string(),
        batch_size: z.number(),
        import_date_range: z.date().optional().nullable(),
        location: z.string().optional().nullable(),
        country_filter_enabled: z.boolean(),
        countries: z.array(z.string()),
        operations: z.array(
          z.object({
            id: z.number(),
            name: z.string(),
            title: z.string(),
            last_executed: z.string().nullable(),
            settings: z.record(z.boolean()),
          }),
        ),
      });
    }, [connector]);

    const badgeType: Record<string, BadgeType> = {
      export: BadgeType.Neutral2,
      import: BadgeType.Neutral4,
    };

    type EditImportDataFormValues = z.infer<typeof editImportSchema>;

    const operationSettingsMap: Record<string, string[]> = {
      import_product: ['auto_product_mapping', 'auto_product_creation'],
      import_order: ['auto_order_import', 'auto_order_confirm'],
      export_stock: ['auto_stock_export'],
      export_fulfillment: ['auto_fulfillment_export', 'notify_customer'],
    };

    const defaultValues = useMemo(
      () => ({
        name: connector.name,
        batch_size: connector.settings?.batch_size,
        import_date_range: connector.settings?.import_date_range,
        location: connector.settings?.location_id ?? null,
        country_filter_enabled: connector.settings?.country_filter_enabled ?? false,
        countries: connector.settings?.country_codes ?? [],
        operations: connector.operations.map((op: any) => ({
          id: op.id,
          name: op.name,
          title: op.title,
          last_executed: op.last_executed,
          settings:
            operationSettingsMap[op.name]?.reduce(
              (acc, key) => ({
                ...acc,
                [key]: connector.settings[key] ?? false,
              }),
              {},
            ) ?? {},
        })),
      }),
      [connector],
    );

    const { data: connectorLocations = [] } = useConnectorLocationsQuery(connector.id);

    const locationOptions = useMemo(
      () => connectorLocations.map((loc: any) => ({ label: loc.name, value: loc.external_id })),
      [connectorLocations],
    );

    const { handleSubmit, control, watch } = useForm<EditImportDataFormValues>({
      resolver: zodResolver(editImportSchema),
      defaultValues,
    });

    const { updateSettingsMutation } = useConnectorMutations();

    const updateSettings = async (
      connectorId: number,
      connectorSettingsUpdate: components['schemas']['ConnectorSettings'],
    ) => {
      try {
        await updateSettingsMutation.mutateAsync(
          {
            params: { path: { connector_id: connectorId } },
            body: connectorSettingsUpdate,
          },
          {
            onSuccess: () => {
              toast.success(t('success.integration_updated'));
            },
          },
        );
      } catch (error) {
        toast.error(t('errors.integration_update_failed'));
      }
    };

    const handleSave = () => {
      const formData = watch();
      const allSettings: Record<string, any> = {};
      formData.operations.forEach((op: any) => {
        Object.entries(op.settings as Record<string, boolean>).forEach(([key, value]) => {
          allSettings[key] = value;
        });
      });
      allSettings.batch_size = +formData.batch_size;
      allSettings.import_date_range = formData.import_date_range;
      allSettings.country_filter_enabled = formData.country_filter_enabled;
      allSettings.country_codes = formData.countries;
      if (formData.location) {
        allSettings.location_id = formData.location;
      }
      updateSettings(connector.id, allSettings as unknown as components['schemas']['ConnectorSettings']);
    };

    const formRef = useRef<HTMLFormElement>(null);

    useImperativeHandle(ref, () => {
      const nativeForm = formRef.current!;
      return {
        ...nativeForm,
        save: handleSave,
      };
    });

    return (
      <form ref={formRef} onSubmit={handleSubmit(onSubmit as any)} className="flex flex-col flex-1 gap-16">
        <div className="flex flex-col">
          <div className="flex flex-col w-full gap-[16px] mb-48">
            <span className="typo-text-16--bold">{t('integrations.batch_size')}</span>
            <ControlledInput
              control={control}
              name="batch_size"
              placeholder={t('integrations.batch_size')}
              disabled={readOnly || !allowEdition}
              className="w-full"
            />
          </div>
          <div className="flex flex-col w-full gap-[16px] mb-48">
            <span className="typo-text-16--bold">{t('general.date_range')}</span>
            <ControlledDateTimePicker control={control} name="import_date_range" disabled={readOnly || !allowEdition} />
          </div>
          {locationOptions.length > 0 && (
            <div className="flex flex-col w-full gap-[16px] mb-48">
              <span className="typo-text-16--bold">{t('integrations.location')}</span>
              <ControlledSelect
                control={control}
                name="location"
                options={locationOptions}
                disabled={readOnly || !allowEdition}
              />
            </div>
          )}

          <div className="flex flex-col w-full gap-[16px] mb-48">
            <div className="flex items-center gap-2">
              <ControlledCheckbox
                control={control}
                name="country_filter_enabled"
                disabled={readOnly || !allowEdition}
              />
              <span className="typo-text-14 pl-3">{t('integrations.country_filter_enabled')}</span>
            </div>
            {watch('country_filter_enabled') && (
              <div className="flex flex-col gap-[16px]">
                <span className="typo-text-16--bold">{t('integrations.allowed_countries')}</span>
                <CountrySelect
                  isControlled
                  isMultiSelect
                  name="countries"
                  control={control}
                  editable={!readOnly && !!allowEdition}
                  className="w-full"
                />
              </div>
            )}
          </div>

          {defaultValues?.operations?.map((operation: any, index: number) => (
            <div key={operation.id} className="flex flex-col gap-[32px] pb-24">
              {operationSettingsMap[operation.name] && (
                <div className="flex items-start justify-between gap-[24px] pt-32 border-subtle-bottom">
                  <div className="flex flex-col gap-[6px]">
                    <div className="flex items-center gap-12">
                      <span className="typo-text-16--bold">{operation.title}</span>
                      {operation.type && (
                        <Badge label={operation.type} expanded type={badgeType[operation.type] ?? BadgeType.Neutral} />
                      )}
                    </div>
                    {operation.description && <p className="typo-text-14 text-faded">{operation.description}</p>}
                  </div>
                </div>
              )}

              {operation.settings &&
                Object.entries(operation.settings).map(([key]) => {
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <ControlledCheckbox
                        control={control}
                        name={`operations.${index}.settings.${key}`}
                        disabled={readOnly || !allowEdition}
                      />
                      <span className="typo-text-14 pl-3">{t(`integrations.${key}`)}</span>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </form>
    );
  },
);
